import { useEffect, useState } from "react";
import { httpDelete, httpGet, httpPost } from "../../../modules/Backend";
import { TableVirtuoso } from "react-virtuoso";
import { Button, TabPanel } from "react-aria-components";
import iconUpload from "../../../assets/icons/icon-upload-white.svg";
import iconEdit from "../../../assets/icons/icon-pen-blue.svg";
import iconDelete from "../../../assets/icons/icon-bin-red.svg";
import iconDownload from "../../../assets/icons/icon-download-white.svg";
import iconDocument from "../../../assets/icons/icon-document-lightblue.svg"
import iconEye from "../../../assets/icons/icon-eye.svg"
import { useNavigate } from "react-router";
import * as Navigation from "../../../modules/Navigation";
import { modalSignal } from "../Modal/ModalContainer";
import ConfirmModal, { ConfirmType } from "../Modal/ConfirmModal";
import { IFilter } from "../../../hooks/UseFilters";
import { hasPermission } from "../../../modules/auth/Permissions/PermissionModule";
import { PermissionContextType, usePermissions } from "../../../modules/auth/Permissions/PermissionContext";
import { PermissionConcerns, PermissionTypes } from "../../../modules/auth/Permissions/PermissionStrings";
import HttpStatusCodes from "../../../modules/HttpStatusCodes";
import LoadingButton from "../../../components/app/LoadingButton"

export interface IDocumentList {
    parentEntityName: string,
    entityName: string,
    i: any,
    filters: IFilter[],
    canAdd?: boolean | ((ctx: PermissionContextType, accountId: string | undefined) => boolean)
}

export default function DocumentList({ parentEntityName, entityName, i, filters, canAdd: canAddProp }: IDocumentList) {
    const [data, setData] = useState<any>(undefined);
    const [resultsKey] = useState<string>(parentEntityName);
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const navigate = useNavigate();
    const permissionsContext = usePermissions();
    const [currentFileViewIndex, setCurrentFileViewIndex] = useState<number | undefined>(undefined);
    const [currentFileDownloadIndex, setCurrentFileDownloadIndex] = useState<number | undefined>(undefined);

    async function getData() {
        const timeout = setTimeout(() => setShowLoader(true), 200)
        const payload = { filters };
        const result = await httpPost(`api/${parentEntityName}/list`, payload);
        if (result?.data) {
            setData(result?.data);
        }
        setShowLoader(false);
        clearTimeout(timeout);
    }

    async function deleteEntity(id: string) {
        const payload = { id }
        const result = await httpDelete(`api/${parentEntityName}/delete`, payload)
        if (result?.status === HttpStatusCodes.Ok) {
            setData({ [resultsKey]: data[resultsKey].filter((x: any) => x.id != id) })
        }
    }

    function handleAddNew() {
        const navPayload = { parentEntityName, entityName }
        Navigation.create.navigate(navigate, parentEntityName, navPayload)
    }

    function handleEdit(id: string) {
        Navigation.edit.navigate(navigate, parentEntityName, id)
    }

    function handleDeleteButtonPress(id: string) {
        modalSignal.value = <ConfirmModal heading={`Delete document?`} information="This action can not be undone" ContinueFunc={() => deleteEntity(id)} type={ConfirmType.Destructive} />
    }

    async function downloadFile(fileData: any, index: number) {
        setCurrentFileDownloadIndex(index);

        if (!fileData || !fileData.url) {
            return;
        }

        const response = await httpGet("api/fileuploads/view?id=" + fileData.id);
        if (response.status === HttpStatusCodes.Ok) {
            const blobResponse = await fetch(fileData.url);
            if (blobResponse.ok) {
                const blob = await blobResponse.blob();
                const link = document.createElement('a');
                const url = window.URL.createObjectURL(blob);
                link.href = url;
                link.download = `${fileData.name}${fileData.type}`;
                document.body.appendChild(link);

                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        }

        setCurrentFileDownloadIndex(undefined);
    }

    async function viewFile(fileData: any, index: number) {
        setCurrentFileViewIndex(index);

        if (!fileData || !fileData.id) {
            return;
        }

        const response = await httpGet("api/fileuploads/view?id=" + fileData.id);
        if (response.status === HttpStatusCodes.Ok) {
            window.open(response.data.url, "_blank");
        }

        setCurrentFileViewIndex(undefined);
    }

    useEffect(() => {
        getData()
    }, [])

    function canAdd(): boolean {
        // If canAdd prop is provided from schema, use it
        if (canAddProp !== undefined) {
            return typeof canAddProp === 'function' ? canAddProp(permissionsContext!, undefined) : canAddProp;
        }
        // Fall back to existing permission check for backward compatibility
        return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Documentation);
    }

    return (<>
        <TabPanel id={i} className={"flex__grow--0"}>
            <div className="display--flex justifyContent--space-between margin__bottom--s">
                <div>
                </div>
                {data && data[resultsKey].length > 0 && canAdd() &&
                    uploadDocumentButton()
                }
            </div>
        </TabPanel>

        <TabPanel id={i}>
            <div className="list__wrapper">
                <div className="list" data-testid="list-wrapper">
                    {!data && renderLoadingList()}
                    {data && data[resultsKey] !== undefined && data[resultsKey].length === 0 && renderNoResultList()}
                    {data && data[resultsKey] !== undefined && data[resultsKey].length > 0 && renderListTable()}
                </div>
            </div>
        </TabPanel>
    </>
    )

    function uploadDocumentButton() {
        return <Button className="button button--small button--primary button--with-icon" onPress={handleAddNew}>
            <img src={iconUpload} />
            <p>Upload new document</p>
        </Button>
    }

    function renderListTable() {
        return (
            <>
                <TableVirtuoso
                    className="react-aria-Table react-aria-TableNoPadding"
                    style={{}}
                    data={data[resultsKey]}
                    initialItemCount={data[resultsKey].length}
                    itemContent={(index, item) => (
                        <>
                            <td className="documents__content-wrapper">
                                <p className="documents__document-title">{item.title}</p>
                                <p className="documents__document-description">{item.description}</p>
                            </td>
                            <td className="documents__right-content-td">
                                <div className="documents__right-content-wrapper">
                                    <div className="documents__right-content">
                                        <p className="documents__document-size">{item.fileSizeFormatted}</p>

                                        {hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Documentation) &&
                                            <LoadingButton className="documents__document-view button button--small button--secondary button--with-icon"
                                                onPress={() => viewFile(item, index)}
                                                isDisabled={currentFileViewIndex === index}
                                                isLoading={currentFileViewIndex === index}
                                                loadingText="Preparing"
                                                buttonText="View">
                                                <img src={iconEye} />
                                            </LoadingButton>
                                        }

                                        {hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Documentation) &&
                                            <LoadingButton className="documents__document-download button button--small button--primary button--with-icon"
                                                onPress={() => downloadFile(item, index)}
                                                isDisabled={currentFileDownloadIndex === index}
                                                isLoading={currentFileDownloadIndex === index}
                                                loadingText="Preparing"
                                                buttonText="Download">
                                                <img src={iconDownload} />
                                            </LoadingButton>
                                        }

                                        {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Documentation) &&
                                            <Button className="documents__document-update button button--small button--secondary button--with-icon" onPress={() => handleEdit(item.id)}>
                                                <img src={iconEdit} />
                                                <p className="button-text">Edit</p>
                                            </Button>
                                        }
                                        {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Documentation) &&
                                            <Button className="documents__document-delete button button--small button--secondary button--secondary--destructive button--with-icon" onPress={() => handleDeleteButtonPress(item.id)}>
                                                <img src={iconDelete} />
                                                <p className="button-text">Delete</p>
                                            </Button>
                                        }
                                    </div>
                                </div>
                            </td>
                        </>
                    )}
                />
            </>
        )
    }

    function renderLoadingList() {
        return (
            <div>
                <div className="react-aria-Table">
                    <table >
                        {showLoader && [...Array(7)].map((e, i) => (
                            <tr>
                                <td>
                                    <div className="loaders loaders__list loaders__list--full-width"></div>
                                </td>
                            </tr>
                        ))}
                    </table>
                </div>
            </div>
        )
    }

    function renderNoResultList() {
        return (
            <div className="documents__no-results-wrapper">
                {!showLoader &&
                    <div className="documents__no-results">
                        <div className="display--flex flex"><img alt="" src={iconDocument} /></div>
                        <p className="documents__no-results-text">No documents</p>
                        {canAdd() &&
                            uploadDocumentButton()
                        }
                    </div>
                }
            </div>
        )
    }
}